import {
  createContext,
  Fragment,
  memo,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Link, useLocation, useNavigationType } from "react-router-dom";
import {
  PiArrowCounterClockwise,
  PiArrowLeft,
  PiClock,
  PiHandTap,
  PiStar,
  PiStarFill,
} from "react-icons/pi";
import { Chapter, Paragraph, Sentence, Story } from "./story";
import {
  apiUrl,
  useSetStoryMarkMutation,
  useStoryListQuery,
  useStoryQuery,
  HttpError,
  isClientHttpError,
} from "./queries";
import { lstr } from "./localization";
import { useLoggedIn } from "./firebase";
import { WORD_EXPLANATION_POPUP_ID, WordExplanationPopup } from "./Explanation";
import {
  GENDER_CLASS,
  GENDER_MARKER_REGEX,
  Gender,
  stripGenderMarkers,
  supportsGenderColoring,
} from "./gender";
import { TextSize } from "./settings";
import {
  isInProgress,
  loadReadingEntries,
  readingProgressByStoryId,
  recordReadingProgress,
} from "./readingProgress";
import { LevelBadge, StoryCover, upNextStories } from "./StoryList";
import { StoryEnd } from "./StoryEnd";
import { SkeletonBlock } from "./Skeleton";
import { Pup } from "./Pup";
import { readAlongSentenceIndexIn, readAlongSentenceProps } from "./ReadAlong";

interface ActivePopup {
  storyId: string;
  l: string;
  r: string;
  lSentenceIdx: number;
  rSentenceIdx: number;
  wordIdx: number;
}

const PopupContext = createContext<{
  activePopup: ActivePopup | null;
  openPopup: (popup: ActivePopup) => void;
  closePopup: () => void;
}>({
  activePopup: null,
  openPopup: () => {},
  closePopup: () => {},
});

function PopupProvider({
  onPopupOpen,
  children,
}: {
  onPopupOpen: () => void;
  children: React.ReactNode;
}) {
  const [activePopup, setActivePopup] = useState<ActivePopup | null>(null);
  const closePopup = useCallback(() => setActivePopup(null), []);
  const openPopup = useCallback(
    (popup: ActivePopup) => {
      setActivePopup(popup);
      onPopupOpen();
    },
    [onPopupOpen],
  );

  useEffect(() => {
    if (!activePopup) return;
    const handleClick = () => closePopup();
    // Escape closes the popup and puts focus back on its word, so keyboard
    // users keep their place in the story wherever focus was.
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      document.querySelector<HTMLElement>("[data-popup-anchor]")?.focus();
      closePopup();
    };
    document.addEventListener("click", handleClick);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("click", handleClick);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [activePopup, closePopup]);

  return (
    <PopupContext.Provider value={{ activePopup, openPopup, closePopup }}>
      {children}
    </PopupContext.Provider>
  );
}

// The story text is a single Tab stop: only the word that last had focus is
// tabbable, and Left/Right arrows move between words. Keyboard users can Tab
// past the whole story in one press and still reach every word.
const WordTabStopContext = createContext<{
  tabStopWordId: string | null;
  setTabStopWordId: (wordId: string) => void;
}>({
  tabStopWordId: null,
  setTabStopWordId: () => {},
});

function wordId(rSentenceIdx: number, wordIdx: number): string {
  return `${rSentenceIdx}:${wordIdx}`;
}

// Moves focus to the previous (-1) or next (+1) word of the story in reading
// order.
function focusAdjacentWord(word: HTMLElement, step: 1 | -1) {
  const storyWords = Array.from(
    word
      .closest("[data-story-text]")
      ?.querySelectorAll<HTMLElement>("[data-word]") ?? [],
  );
  storyWords[storyWords.indexOf(word) + step]?.focus();
}

function Image({ storyId, imageId }: { storyId: string; imageId?: string }) {
  if (!imageId) {
    return <></>;
  }
  let imageUrl = apiUrl("/image");
  imageUrl.searchParams.append("story_id", storyId);
  imageUrl.searchParams.append("id", imageId);
  return (
    <div className="relative mb-7 w-full overflow-hidden rounded-2xl pb-[42%] shadow-lift">
      <img
        alt=""
        src={imageUrl.toString()}
        className="absolute left-0 top-1/2 h-auto w-full -translate-y-1/2 object-cover"
      />
    </div>
  );
}

// Extracts the gender marker (if any) from a single token and returns the
// token with the marker stripped. Returns the marker letter (m/f/n) or null
// when the token has no marker. Used by both the interactive and
// non-interactive branches in SentenceView so markers NEVER reach the DOM.
function splitGenderMarker(token: string): {
  cleanToken: string;
  gender: Gender | null;
} {
  const match = token.match(GENDER_MARKER_REGEX);
  // Reset lastIndex because GENDER_MARKER_REGEX is a /g regex and
  // `match` on a /g regex doesn't have capture groups; we read the letter
  // out of the matched string itself ("{m}" -> "m").
  if (!match || match.length === 0) {
    return { cleanToken: token, gender: null };
  }
  const gender = match[0][1] as Gender;
  return { cleanToken: token.replace(GENDER_MARKER_REGEX, ""), gender };
}

function SentenceView({
  text,
  storyId,
  l,
  r,
  lSentence,
  rSentence,
  interactive,
  applyGenderColors,
  beingReadAloud = false,
}: {
  text: string;
  storyId: string;
  l: string;
  r: string;
  lSentence: Sentence;
  rSentence: Sentence;
  interactive: boolean;
  // When true, tokens carrying a {m/f/n} marker are tinted with the
  // gender-specific class. When false the markers are still stripped, just
  // not coloured (used for the L-side / translated text and for stories in
  // locales that don't support gender markers at all).
  applyGenderColors: boolean;
  // Whether read-along is reading this (learned-language) sentence.
  beingReadAloud?: boolean;
}) {
  const { activePopup, openPopup, closePopup } = useContext(PopupContext);
  const { tabStopWordId, setTabStopWordId } = useContext(WordTabStopContext);

  const onWordClick = useCallback(
    (e: React.MouseEvent | React.KeyboardEvent, wordIdx: number) => {
      // Skip the popup when the click is really the end of a drag-select:
      // we want users to be able to highlight prose for copy / external
      // lookup without hijacking the release into an explanation popup.
      const selection = window.getSelection();
      if (selection && selection.toString().length > 0) {
        return;
      }
      e.stopPropagation();
      // While an explanation is open, a click on any word (including another
      // one) only dismisses it. Clicks meant to close the popup often land on
      // a neighbouring word by accident, and opening a new explanation there
      // is never what the user wanted.
      if (activePopup) {
        closePopup();
        return;
      }
      openPopup({
        storyId,
        l,
        r,
        lSentenceIdx: lSentence.index,
        rSentenceIdx: rSentence.index,
        wordIdx,
      });
    },
    [
      storyId,
      l,
      r,
      lSentence.index,
      rSentence.index,
      activePopup,
      openPopup,
      closePopup,
    ],
  );

  if (!interactive) {
    // Strip markers but do not colour: this branch is used for the L-side
    // translation row, which is markerless in practice but we strip
    // defensively to avoid rendering a literal "{n}" if anything ever
    // slipped through.
    return <span>{stripGenderMarkers(text)}</span>;
  }

  const isActiveInThisSentence =
    activePopup?.lSentenceIdx === lSentence.index &&
    activePopup?.rSentenceIdx === rSentence.index;

  const tokens = text.split(/(\s+)/);
  let wordIdx = 0;

  return (
    <span {...readAlongSentenceProps(rSentence.index, beingReadAloud)}>
      {tokens.map((token, idx) => {
        if (/^\s+$/.test(token) || token === "") {
          return <Fragment key={idx}>{token}</Fragment>;
        }

        const currentWordIdx = wordIdx++;
        const currentWordId = wordId(rSentence.index, currentWordIdx);
        // Strip the gender marker BEFORE splitting punctuation, because the
        // backend strips markers before extracting the word too - keeping
        // both sides in lock-step is what makes the word_idx match.
        const { cleanToken, gender } = splitGenderMarker(token);
        const leadingPunctuation = cleanToken.match(/^\p{P}+/u)?.[0] ?? "";
        // A punctuation-only token (e.g. a spaced dash) would match both the
        // leading and the trailing pattern and render twice; it is kept whole
        // as the word instead, as the backend's ExtractWord does.
        const punctuationOnly = leadingPunctuation.length === cleanToken.length;
        const leading = punctuationOnly ? "" : leadingPunctuation;
        const trailing = punctuationOnly
          ? ""
          : cleanToken.match(/\p{P}+$/u)?.[0] ?? "";
        const word = punctuationOnly
          ? cleanToken
          : cleanToken.slice(
              leading.length,
              cleanToken.length - trailing.length,
            );
        const isActive =
          isActiveInThisSentence && activePopup?.wordIdx === currentWordIdx;
        const genderClass =
          applyGenderColors && gender ? GENDER_CLASS[gender] : "";
        // While any popup is open a word click only dismisses it, so drop the
        // hover highlight and pointer cursor: keeping them would promise an
        // explanation that the click won't deliver. The explicit cursor-auto
        // is required because Tailwind's preflight gives [role="button"]
        // elements cursor:pointer even without the utility class.
        const clickAffordanceClass = activePopup
          ? "cursor-auto"
          : "cursor-pointer hover:bg-highlight-light";

        return (
          <Fragment key={idx}>
            {leading}
            {/* The popup is the word's sibling, not its child: inside the
                role="button" span its controls would be hidden from screen
                readers, and their Enter/Space would reach the word's key
                handler. The wrapper is always rendered so the word keeps its
                DOM node (and focus) when the popup opens; while active it is
                the popup's positioning anchor. */}
            <span className={isActive ? "relative" : undefined}>
              <span
                role="button"
                aria-haspopup="dialog"
                aria-expanded={isActive}
                aria-controls={isActive ? WORD_EXPLANATION_POPUP_ID : undefined}
                tabIndex={currentWordId === tabStopWordId ? 0 : -1}
                data-word
                data-popup-anchor={isActive || undefined}
                className={`${clickAffordanceClass} rounded-[4px] px-[1px] transition-colors duration-150 ${
                  isActive ? "bg-highlight text-on-highlight" : genderClass
                }`}
                onClick={(e) => onWordClick(e, currentWordIdx)}
                onFocus={() => setTabStopWordId(currentWordId)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    onWordClick(e, currentWordIdx);
                  } else if (e.key === "ArrowRight" || e.key === "ArrowLeft") {
                    e.preventDefault();
                    focusAdjacentWord(
                      e.currentTarget,
                      e.key === "ArrowRight" ? 1 : -1,
                    );
                  }
                }}
              >
                {word}
              </span>
              {isActive && (
                <WordExplanationPopup
                  storyId={storyId}
                  l={l}
                  r={r}
                  lSentenceIdx={lSentence.index}
                  rSentenceIdx={rSentence.index}
                  wordIdx={currentWordIdx}
                  word={word}
                  wordClassName={genderClass}
                />
              )}
            </span>
            {trailing}
          </Fragment>
        );
      })}
    </span>
  );
}

// Translation text is set a little smaller and quieter than the original,
// hung off a thin rule, so the eye stays on the learned language.
const TRANSLATION_CLASS =
  "border-l-2 border-primary/25 pl-2.5 text-[0.88em] leading-[1.65] text-secondary-text sm:pl-3.5";

const ParagraphView = memo(function ParagraphView({
  storyId,
  l,
  r,
  lParagraph,
  rParagraph,
  shouldShowTranslation,
  showTranslationBySentence,
  applyGenderColors,
  readAlongSentenceIndex,
}: {
  storyId: string;
  l: string;
  r: string;
  lParagraph: Paragraph;
  rParagraph: Paragraph;
  shouldShowTranslation: boolean;
  showTranslationBySentence: boolean;
  applyGenderColors: boolean;
  // The sentence being read aloud, if it is in this paragraph.
  readAlongSentenceIndex: number | null;
}) {
  if (lParagraph.sentences.length !== rParagraph.sentences.length) {
    console.error("Paragraph sentences are not aligned");
    return <div className="text-error">{lstr(l).story_alignment_error}</div>;
  }

  // Text is left-aligned rather than justified: in a phone-width column
  // justification stretches word gaps unevenly (badly so with German
  // compounds), which disrupts reading. The lang attributes give screen
  // readers the right pronunciation for each language.
  return (
    <div>
      <Image storyId={storyId} imageId={rParagraph.imageId} />
      {(!shouldShowTranslation || !showTranslationBySentence) && (
        <div lang={r} className="text-main-text">
          {rParagraph.sentences.map((sentence, index) => (
            <Fragment key={index}>
              <SentenceView
                text={sentence.text}
                storyId={storyId}
                l={l}
                r={r}
                lSentence={lParagraph.sentences[index]}
                rSentence={sentence}
                interactive={true}
                applyGenderColors={applyGenderColors}
                beingReadAloud={sentence.index === readAlongSentenceIndex}
              />
              {index < rParagraph.sentences.length - 1 && " "}
            </Fragment>
          ))}
        </div>
      )}
      {shouldShowTranslation && !showTranslationBySentence && (
        <div lang={l} className={`mt-2.5 ${TRANSLATION_CLASS}`}>
          {lParagraph.sentences.map((sentence, index) => (
            <Fragment key={index}>
              <SentenceView
                text={sentence.text}
                storyId={storyId}
                l={l}
                r={r}
                lSentence={sentence}
                rSentence={rParagraph.sentences[index]}
                interactive={false}
                applyGenderColors={false}
              />
              {index < lParagraph.sentences.length - 1 && " "}
            </Fragment>
          ))}
        </div>
      )}
      {shouldShowTranslation && showTranslationBySentence && (
        <div className="flex flex-col gap-3.5">
          {rParagraph.sentences.map((sentence, index) => (
            <div key={index}>
              <div lang={r} className="text-main-text">
                <SentenceView
                  text={sentence.text}
                  storyId={storyId}
                  l={l}
                  r={r}
                  lSentence={lParagraph.sentences[index]}
                  rSentence={sentence}
                  interactive={true}
                  applyGenderColors={applyGenderColors}
                  beingReadAloud={sentence.index === readAlongSentenceIndex}
                />
              </div>
              <div lang={l} className={`mt-1 ${TRANSLATION_CLASS}`}>
                <SentenceView
                  text={lParagraph.sentences[index].text}
                  storyId={storyId}
                  l={l}
                  r={r}
                  lSentence={lParagraph.sentences[index]}
                  rSentence={rParagraph.sentences[index]}
                  interactive={false}
                  applyGenderColors={false}
                />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
});

const ChapterView = memo(function ChapterView({
  storyId,
  l,
  r,
  lChapter,
  rChapter,
  shouldShowTranslation,
  showTranslationBySentence,
  applyGenderColors,
  readAlongSentenceIndex,
}: {
  storyId: string;
  l: string;
  r: string;
  lChapter: Chapter;
  rChapter: Chapter;
  shouldShowTranslation: boolean;
  showTranslationBySentence: boolean;
  applyGenderColors: boolean;
  // The sentence being read aloud, if it is in this chapter.
  readAlongSentenceIndex: number | null;
}) {
  if (lChapter.paragraphs.length !== rChapter.paragraphs.length) {
    console.error("Chapter paragraphs are not aligned");
    return <div className="text-error">{lstr(l).story_alignment_error}</div>;
  }

  // Full class names on purpose: Tailwind's JIT scanner can't see classes
  // assembled from runtime template strings like `gap-${n}`.
  const paragraphGapClass = shouldShowTranslation ? "gap-8" : "gap-5";

  return (
    <div>
      {rChapter.title && (
        <div className="mb-6 mt-4 flex items-center gap-4">
          <span aria-hidden="true" className="h-px flex-1 bg-border" />
          <h2
            lang={r}
            className="font-literata text-[1.05em] font-semibold tracking-wide text-main-text"
          >
            {rChapter.title}
          </h2>
          <span aria-hidden="true" className="h-px flex-1 bg-border" />
        </div>
      )}
      <div className={`flex flex-col ${paragraphGapClass}`}>
        {rChapter.paragraphs.map((paragraph, index) => (
          <ParagraphView
            key={index}
            storyId={storyId}
            l={l}
            r={r}
            lParagraph={lChapter.paragraphs[index]}
            rParagraph={paragraph}
            shouldShowTranslation={shouldShowTranslation}
            showTranslationBySentence={showTranslationBySentence}
            applyGenderColors={applyGenderColors}
            readAlongSentenceIndex={readAlongSentenceIndexIn(
              [paragraph],
              readAlongSentenceIndex,
            )}
          />
        ))}
      </div>
    </div>
  );
});

// Reading text sizes; the story column scales everything else in em.
const TEXT_SIZE_CLASS: Record<TextSize, string> = {
  [TextSize.Small]: "text-[15.2px] sm:text-[16.2px]",
  [TextSize.Medium]: "text-[17.1px] sm:text-[18px]",
  [TextSize.Large]: "text-[19px] sm:text-[21px]",
};

// A learner's reading pace, for the "N min read" estimate.
const LEARNER_WORDS_PER_MINUTE = 100;

function readingMinutes(story: Story): number {
  let words = 0;
  for (const chapter of story.chapters) {
    for (const paragraph of chapter.paragraphs) {
      for (const sentence of paragraph.sentences) {
        words += sentence.text.split(/\s+/).filter(Boolean).length;
      }
    }
  }
  return Math.max(1, Math.round(words / LEARNER_WORDS_PER_MINUTE));
}

// The tap-a-word hint is shown until the reader has opened one explanation
// (or dismissed it); this device remembers that.
const TAP_WORD_HINT_SEEN_KEY = "tapWordHintSeen";

function loadTapWordHintSeen(): boolean {
  try {
    return localStorage.getItem(TAP_WORD_HINT_SEEN_KEY) === "1";
  } catch {
    return false;
  }
}

function storeTapWordHintSeen() {
  try {
    localStorage.setItem(TAP_WORD_HINT_SEEN_KEY, "1");
  } catch (err) {
    console.error("Failed to store the tap-a-word hint state", err);
  }
}

// Toast is a small message floating at the bottom of the screen.
function Toast({ children }: { children: React.ReactNode }) {
  return (
    <div className="pointer-events-none fixed inset-x-0 bottom-0 z-40 flex justify-center px-4 pb-[calc(1.25rem+env(safe-area-inset-bottom))]">
      <div
        role="status"
        className="animate-rise pointer-events-auto flex max-w-md items-center gap-3 rounded-full bg-main-text py-2 pl-4 pr-2 text-sm text-cream shadow-pop"
      >
        {children}
      </div>
    </div>
  );
}

function toastButtonClass() {
  return "shrink-0 rounded-full bg-cream/15 px-3 py-1.5 text-xs font-semibold text-cream transition-colors hover:bg-cream/25";
}

// Where in the story the reader is: the fraction of the text above the
// bottom of the screen.
function readingPosition(text: HTMLElement): number {
  const rect = text.getBoundingClientRect();
  if (rect.height <= 0) return 0;
  return Math.min(
    1,
    Math.max(0, (window.innerHeight - rect.top) / rect.height),
  );
}

// How long the "picked up where you left off" toast stays.
const RESUME_TOAST_MS = 6000;

function StoryView({
  storyId,
  l,
  r,
  shouldShowTranslation,
  showTranslationBySentence,
  colorNounGenders,
  textSize,
  readAlongSentenceIndex,
  onWordPopupOpen,
}: {
  storyId: string;
  l: string;
  r: string;
  shouldShowTranslation: boolean;
  showTranslationBySentence: boolean;
  // User-facing toggle. The effective decision (`applyGenderColors`) also
  // checks that the learned-language locale actually supports gender markers
  // - we honour the flag even when R doesn't support gender, because flipping
  // it off should still strip any stray markers in older content.
  colorNounGenders: boolean;
  textSize: TextSize;
  // The learned-language sentence read-along is reading, if any.
  readAlongSentenceIndex: number | null;
  // Called when a word's explanation opens. Read-along pauses then, so it
  // neither talks over the popup's Listen nor scrolls the word away.
  onWordPopupOpen: () => void;
}) {
  const strings = lstr(l);
  const query = useStoryQuery(storyId, l, r);
  // The built-in story list supplies the level and what to read next; it
  // is usually cached from the home page.
  const storyListQuery = useStoryListQuery(l, r);
  const loggedIn = useLoggedIn();
  const navigationType = useNavigationType();
  const location = useLocation();
  const favoriteMutation = useSetStoryMarkMutation("favorite");
  const readMutation = useSetStoryMarkMutation("read");
  // null until a word gets focus; the first word is the tab stop until then.
  const [tabStopWordId, setTabStopWordId] = useState<string | null>(null);
  const [tapWordHintVisible, setTapWordHintVisible] = useState(false);
  const [resumed, setResumed] = useState(false);
  const textRef = useRef<HTMLDivElement>(null);
  // Read once when the story opens; the end-of-story suggestions use it.
  const progressByStoryId = useMemo(
    () => readingProgressByStoryId(loadReadingEntries()),
    [],
  );

  const rStory = query.data?.localizations.get(r);
  const lStoryMaybe = query.data?.localizations.get(l);

  const dismissTapWordHint = useCallback(() => {
    setTapWordHintVisible(false);
    storeTapWordHintSeen();
  }, []);
  // Opening any explanation shows the reader has found the feature.
  const handleWordPopupOpen = useCallback(() => {
    dismissTapWordHint();
    onWordPopupOpen();
  }, [dismissTapWordHint, onWordPopupOpen]);

  // The hint appears a moment after the story does, once the reader has
  // had a look at the page.
  useEffect(() => {
    if (!rStory || loadTapWordHintSeen()) return;
    const timeout = setTimeout(() => setTapWordHintVisible(true), 1200);
    return () => clearTimeout(timeout);
  }, [rStory]);

  // Remember the reading position on this device while reading, for
  // "Continue reading" and the progress bars in the story lists.
  useEffect(() => {
    const text = textRef.current;
    if (!rStory || !text) return;
    const rTitle = rStory.title;
    const lTitle = lStoryMaybe?.title ?? null;
    let pendingPosition: number | null = null;
    let timeout: ReturnType<typeof setTimeout> | undefined;
    const save = () => {
      timeout = undefined;
      if (pendingPosition === null) return;
      recordReadingProgress({
        storyId,
        r,
        rTitle,
        lTitle,
        progress: pendingPosition,
      });
      pendingPosition = null;
    };
    const onScroll = () => {
      pendingPosition = readingPosition(text);
      if (timeout === undefined) timeout = setTimeout(save, 500);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("pagehide", save);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("pagehide", save);
      if (timeout !== undefined) clearTimeout(timeout);
      save();
    };
  }, [rStory, lStoryMaybe, storyId, r]);

  // Opening a story that was left halfway continues where the reader
  // stopped, with a bit of the text before it in view. Back/Forward within
  // the app keeps its own restored position instead; a page load (also a
  // "POP", of the initial "default" entry) resumes too.
  const resumeCheckedRef = useRef(false);
  useEffect(() => {
    const text = textRef.current;
    if (resumeCheckedRef.current || !rStory || !text) return;
    resumeCheckedRef.current = true;
    if (navigationType === "POP" && location.key !== "default") return;
    const entry = loadReadingEntries().find((e) => e.storyId === storyId);
    if (!entry || !isInProgress(entry.progress)) return;
    const frame = requestAnimationFrame(() => {
      const textTop = text.getBoundingClientRect().top + window.scrollY;
      const target =
        textTop + entry.progress * text.offsetHeight - window.innerHeight * 0.6;
      if (target > 0) {
        window.scrollTo(0, target);
        setResumed(true);
      }
    });
    return () => cancelAnimationFrame(frame);
  }, [rStory, storyId, navigationType, location.key]);

  useEffect(() => {
    if (!resumed) return;
    const timeout = setTimeout(() => setResumed(false), RESUME_TOAST_MS);
    return () => clearTimeout(timeout);
  }, [resumed]);

  if (query.isPending) {
    return (
      <div
        className="flex w-full flex-col items-center gap-4 pt-6"
        aria-busy="true"
      >
        <span className="sr-only">{strings.loading_story}</span>
        <SkeletonBlock className="h-24 w-[72px] rounded-lg" />
        <SkeletonBlock className="h-8 w-2/3" />
        <SkeletonBlock className="h-4 w-24" />
        <div className="mt-8 flex w-full flex-col gap-3">
          {Array.from({ length: 7 }, (_, idx) => (
            <SkeletonBlock
              key={idx}
              className={`h-4 ${idx % 3 === 2 ? "w-4/5" : "w-full"}`}
            />
          ))}
        </div>
      </div>
    );
  }

  if (query.isError || !rStory) {
    if (!query.isError) {
      console.error("Story is missing the learned-language localization");
    }
    // A user's own story answers 401 to a signed-out reader (its owner,
    // logged out) and 404 to everyone else. Any other client error (a wrong
    // link) offers the way back to the library instead of a retry.
    const loginRequired =
      query.error instanceof HttpError && query.error.status === 401;
    const notFound = !query.isError || isClientHttpError(query.error);
    return (
      <div className="flex w-full flex-col items-center gap-4 pt-12 text-center">
        <Pup shape="circle" className="h-20 w-20" />
        <p className="font-literata text-xl font-semibold text-main-text">
          {loginRequired
            ? strings.story_login_required
            : notFound
              ? strings.story_not_found_error
              : strings.loading_story_error}
        </p>
        {loginRequired ? (
          <Link to="/login" className="btn-primary px-5 py-2.5">
            {strings.login_button}
          </Link>
        ) : notFound ? (
          <Link to="/" className="btn-primary px-5 py-2.5">
            <PiArrowLeft size={18} />
            {strings.story_back_to_library}
          </Link>
        ) : (
          <button
            type="button"
            onClick={() => query.refetch()}
            className="btn-secondary px-5 py-2.5"
          >
            <PiArrowCounterClockwise size={16} />
            {strings.story_load_retry}
          </button>
        )}
      </div>
    );
  }

  // The L localization is optional. When absent, we render
  // r-only and disable the translation layer.
  const hasLStory = lStoryMaybe !== undefined;
  const lStory = lStoryMaybe ?? rStory;
  const effectiveShowTranslation = shouldShowTranslation && hasLStory;

  if (lStory.chapters.length !== rStory.chapters.length) {
    console.error("Story chapters are not aligned");
    return <div className="text-error">{strings.story_alignment_error}</div>;
  }

  const applyGenderColors = colorNounGenders && supportsGenderColoring(r);
  const firstSentence = rStory.chapters[0]?.paragraphs[0]?.sentences[0];
  const effectiveTabStopWordId =
    tabStopWordId ?? (firstSentence ? wordId(firstSentence.index, 0) : null);
  const descriptor = storyListQuery.data?.find((s) => s.id === storyId);
  const upNext = storyListQuery.data
    ? upNextStories(storyListQuery.data, storyId, progressByStoryId, 3)
    : [];

  return (
    <PopupProvider onPopupOpen={handleWordPopupOpen}>
      <WordTabStopContext.Provider
        value={{ tabStopWordId: effectiveTabStopWordId, setTabStopWordId }}
      >
        <article className={TEXT_SIZE_CLASS[textSize]}>
          <header className="flex flex-col items-center text-center">
            {rStory.imageId ? (
              <Image storyId={storyId} imageId={rStory.imageId} />
            ) : (
              <StoryCover
                storyId={storyId}
                className="mb-6 mt-2 h-24 w-[72px] rounded-lg shadow-lift"
              />
            )}
            <h1
              lang={r}
              className="font-literata text-[28px] font-bold leading-tight tracking-tight text-main-text [text-wrap:balance] sm:text-[34px]"
            >
              {rStory.title}
            </h1>
            {effectiveShowTranslation && (
              <p
                lang={l}
                className="mt-1.5 font-literata text-lg text-secondary-text [text-wrap:balance]"
              >
                {lStory.title}
              </p>
            )}
            <div className="mt-4 flex items-center gap-3 text-sm text-secondary-text">
              {descriptor && <LevelBadge level={descriptor.level} />}
              <span className="inline-flex items-center gap-1.5">
                <PiClock size={15} />
                {strings.story_minutes.replace(
                  "{count}",
                  String(readingMinutes(rStory)),
                )}
              </span>
              {loggedIn && (
                <button
                  type="button"
                  aria-label={
                    query.data.favorite
                      ? strings.unfavorite_story_button_label
                      : strings.favorite_story_button_label
                  }
                  title={
                    query.data.favorite
                      ? strings.unfavorite_story_button_label
                      : strings.favorite_story_button_label
                  }
                  aria-pressed={query.data.favorite}
                  className={`-my-1 flex h-8 w-8 items-center justify-center rounded-full transition-colors hover:bg-cream-dark ${
                    query.data.favorite
                      ? "text-amber-400 hover:text-amber-500"
                      : "text-secondary-text hover:text-amber-500"
                  }`}
                  onClick={() =>
                    favoriteMutation.mutate({
                      storyId,
                      marked: !query.data.favorite,
                    })
                  }
                >
                  {query.data.favorite ? (
                    <PiStarFill size={19} />
                  ) : (
                    <PiStar size={19} />
                  )}
                </button>
              )}
            </div>
            {favoriteMutation.isError && (
              <p role="alert" className="mt-2 text-sm text-error">
                {strings.favorite_story_error}
              </p>
            )}
          </header>

          <div
            ref={textRef}
            data-story-text
            className="mt-10 flex flex-col gap-10 font-literata leading-[1.75] [font-feature-settings:'kern','liga']"
          >
            {rStory.chapters.map((chapter, index) => (
              <ChapterView
                key={index}
                storyId={storyId}
                l={l}
                r={r}
                lChapter={lStory.chapters[index]}
                rChapter={chapter}
                shouldShowTranslation={effectiveShowTranslation}
                showTranslationBySentence={showTranslationBySentence}
                applyGenderColors={applyGenderColors}
                readAlongSentenceIndex={readAlongSentenceIndexIn(
                  chapter.paragraphs,
                  readAlongSentenceIndex,
                )}
              />
            ))}
          </div>
        </article>

        <StoryEnd
          l={l}
          r={r}
          loggedIn={loggedIn}
          read={query.data.read}
          onSetRead={(read) => readMutation.mutate({ storyId, marked: read })}
          readError={readMutation.isError}
          upNext={upNext}
          progressByStoryId={progressByStoryId}
        />

        {tapWordHintVisible && (
          <Toast>
            <PiHandTap size={19} className="animate-nudge shrink-0" />
            <span>{strings.coach_tap_word}</span>
            <button
              type="button"
              onClick={dismissTapWordHint}
              className={toastButtonClass()}
            >
              {strings.coach_got_it}
            </button>
          </Toast>
        )}
        {resumed && !tapWordHintVisible && (
          <Toast>
            <span>{strings.resume_toast}</span>
            <button
              type="button"
              onClick={() => {
                setResumed(false);
                window.scrollTo({ top: 0, behavior: "smooth" });
              }}
              className={toastButtonClass()}
            >
              {strings.resume_start_over}
            </button>
          </Toast>
        )}
      </WordTabStopContext.Provider>
    </PopupProvider>
  );
}

export default StoryView;
